import React, { useState } from 'react';
import cc from 'classcat';
import { DateTime } from 'luxon';
import { LeaveRequestUserInfoIncluded } from '../../commonTypes/acs';
import { Button, RequestStatus, Modal, MediaWidth, Table, TableCell, TableHeadCell, TableRow, Pagination, IPaginationProps } from '../Common';
import { useResizeListener } from '../../hooks/windowEvents/useResizeListener';
import { convertIsoToDiffDaysWithoutWeekendsAndHolidays, convertIsoToMonthDayInterval } from '../../util/date/dateHelper';
import { RequestAccordion } from '../LeaveRequestPartials';
import { LeaveRequestDto, LeaveRequestStatus, LeaveRequestType } from '../../commonTypes/leaveRequest.types';
import { HolidayDto } from '../../commonTypes/organization.types';
import { useTranslation } from '../../hooks/useTranslation';
import Linkify from 'linkify-react';
import { formatToMonthDay } from '../../util/date/dateFormater';
import { MedicalLeaveCellHead } from '../../commonTypes/calendar.types';
import EditOngoingLeaveRequest from '../modalContent/EditOngoingLeaveRequest';
import RequestMedicalLeave from '../modalContent/RequestLeave/RequestMedicalLeave';

interface IRequestedDays {
    requests: LeaveRequestUserInfoIncluded[];
    viewAll: boolean;
    setViewAll: React.Dispatch<React.SetStateAction<boolean>>;
    paginationProps: IPaginationProps;
    openRejectDetailsModal: (reason?: string) => void;
    openDeleteModal: (id: string, requestType?: LeaveRequestType) => void;
    editOngoingLeaveRequestCb: (request: LeaveRequestDto) => void;
    holidays: HolidayDto[];
    title: string;
}

const RequestedDays = ({
    requests,
    viewAll,
    setViewAll,
    paginationProps,
    openRejectDetailsModal,
    openDeleteModal,
    editOngoingLeaveRequestCb,
    holidays,
    title,
}: IRequestedDays) => {
    const { t } = useTranslation();
    const hasRequests = requests.length !== 0;
    const { isMaxWidthLarge: isMobileView, isMaxWidthXLarge } = useResizeListener();

    const [requestToEdit, setRequestToEdit] = useState<LeaveRequestUserInfoIncluded | null>(null);
    const [medicalRequestToEdit, setMedicalRequestToEdit] = useState<MedicalLeaveCellHead | null>(null);
    const [isEditOngoingModalVisible, setIsEditOngoingModalVisible] = useState(false);
    const [isEditMedicalModalVisible, setIsEditMedicalModalVisible] = useState(false);

    const isMedicalLeaveRequest = (request: LeaveRequestUserInfoIncluded) => request.policyType === LeaveRequestType.MEDICAL_LEAVE;
    const hasMedicalLeave = requests.some(isMedicalLeaveRequest);

    const getMinimizeText = (minimize: string, minimizeRequest: string, seeAllRequests: string) => {
        if (viewAll && isMobileView) return minimize;
        else if (viewAll) return minimizeRequest;
        return seeAllRequests;
    };

    const isDeletionEnabled = (request: LeaveRequestUserInfoIncluded) => {
        if (request.status === LeaveRequestStatus.REJECTED) return false;

        const fromDate = DateTime.fromISO(request.fromDate);
        const isRequestInFuture = fromDate.startOf('day') > DateTime.now().startOf('day');

        if (request.policyType === LeaveRequestType.MEDICAL_LEAVE) {
            return request.status === LeaveRequestStatus.PENDING || isRequestInFuture;
        }

        if (hasMedicalLeave) return false;
        return request.status === LeaveRequestStatus.PENDING || isRequestInFuture;
    };

    const isEditEnabled = (request: LeaveRequestUserInfoIncluded) => {
        const now = DateTime.now();
        const fromDate = DateTime.fromISO(request.fromDate).startOf('day');
        const toDate = DateTime.fromISO(request.toDate).endOf('day');
        return fromDate <= now && now <= toDate && request.status === LeaveRequestStatus.APPROVED;
    };

    const openEditModal = (request: LeaveRequestUserInfoIncluded | MedicalLeaveCellHead) => {
        if ('policyType' in request && request.policyType === LeaveRequestType.MEDICAL_LEAVE) {
            setMedicalRequestToEdit(request as MedicalLeaveCellHead);
            setIsEditMedicalModalVisible(true);
        } else {
            setRequestToEdit(request as LeaveRequestUserInfoIncluded);
            setIsEditOngoingModalVisible(true);
        }
    };

    const handleCloseEditOngoingModal = () => {
        setIsEditOngoingModalVisible(false);
        setRequestToEdit(null);
    };

    const handleCloseEditMedicalModal = () => {
        setIsEditMedicalModalVisible(false);
        setMedicalRequestToEdit(null);
    };

    return (
        <div className={cc(['requested-days', { 'requested-days--all': viewAll }, { 'requested-days--empty': !hasRequests }])}>
            <div className="requested-days__header">
                <h4>{title}</h4>
            </div>
            <MediaWidth min="lg">
                <Table className="requested-days__table">
                    <TableRow>
                        <TableHeadCell>{t('leaveRequest.requestDate')}</TableHeadCell>
                        <TableHeadCell>{t('leaveRequest.policy')}</TableHeadCell>
                        <TableHeadCell>{t('leaveRequest.period')}</TableHeadCell>
                        <TableHeadCell>{t('leaveRequest.capitalize_days')}</TableHeadCell>
                        {!isMaxWidthXLarge && <TableHeadCell>{t('common.description')}</TableHeadCell>}
                        <TableHeadCell>{t('leaveRequest.status')}</TableHeadCell>
                    </TableRow>
                    {hasRequests &&
                        requests.map(req => (
                            <TableRow key={req.id}>
                                <TableCell>{formatToMonthDay(req.createdAt)}</TableCell>
                                <TableCell className="requested-days__policy">{req.policyType}</TableCell>
                                <TableCell>
                                    {req.policyType === LeaveRequestType.MEDICAL_LEAVE && req.toDate === 'Present'
                                        ? `${formatToMonthDay(req.fromDate)} - Present`
                                        : convertIsoToMonthDayInterval(req.fromDate, req.toDate)}
                                </TableCell>
                                <TableCell>
                                    {req.policyType === LeaveRequestType.MEDICAL_LEAVE && req.toDate === 'Present'
                                        ? '/'
                                        : `${convertIsoToDiffDaysWithoutWeekendsAndHolidays(
                                              req.fromDate,
                                              req.toDate,
                                              holidays,
                                              req.leaveDurationType
                                          )} ${t('leaveRequest.dayUnit')}`}
                                </TableCell>
                                {!isMaxWidthXLarge && (
                                    <TableCell>
                                        <Linkify>{req.description}</Linkify>
                                    </TableCell>
                                )}
                                <TableCell>
                                    <RequestStatus status={req.status} reason={req.rejectReason} openDetails={openRejectDetailsModal}>
                                        <div className="requested-days__edit-delete">
                                            {isEditEnabled(req) && (
                                                <button onClick={() => openEditModal(req)}>
                                                    <img src="/svg/pen-grey.svg" alt="Pen" />
                                                </button>
                                            )}
                                            {isDeletionEnabled(req) && (
                                                <button onClick={() => openDeleteModal(req.id, req.policyType as LeaveRequestType)}>
                                                    <img src="/svg/close-square.svg" alt="Remove" />
                                                </button>
                                            )}
                                        </div>
                                    </RequestStatus>
                                </TableCell>
                            </TableRow>
                        ))}
                </Table>
            </MediaWidth>
            <MediaWidth max="lg">
                <div>
                    {requests.map(req => (
                        <RequestAccordion key={req.id} request={req} openDeleteModal={openDeleteModal} openEditModal={() => openEditModal(req)} />
                    ))}
                </div>
            </MediaWidth>
            {!hasRequests && (
                <div className="requested-days__empty-label">
                    {title === t('medicalLeaveRequest.medicalLeaveRequests')
                        ? t('medicalLeaveRequest.noMedicalLeaveRequests')
                        : 'You currently have no requests for day off, your list is empty.'}
                </div>
            )}
            {hasRequests && (
                <div className={cc(['requested-days__footer', { 'requested-days__footer--all': viewAll }])}>
                    <Button
                        variant="text"
                        color="blue"
                        onClick={() => setViewAll(prev => !prev)}
                        text={getMinimizeText(t('leaveRequest.minimize'), t('leaveRequest.minimizeRequests'), t('leaveRequest.seeAllRequests'))}
                    />
                    {viewAll && <Pagination {...paginationProps} />}
                </div>
            )}

            <Modal isVisible={isEditOngoingModalVisible} closeModal={handleCloseEditOngoingModal}>
                {requestToEdit && (
                    <EditOngoingLeaveRequest
                        close={handleCloseEditOngoingModal}
                        request={requestToEdit}
                        editOngoingLeaveRequestCb={editOngoingLeaveRequestCb}
                    />
                )}
            </Modal>

            <Modal isVisible={isEditMedicalModalVisible} closeModal={handleCloseEditMedicalModal}>
                {medicalRequestToEdit && (
                    <RequestMedicalLeave
                        close={handleCloseEditMedicalModal}
                        medicalLeaveToEnd={medicalRequestToEdit}
                        isEditMedical={true}
                        heading="Edit Medical Leave"
                    />
                )}
            </Modal>
        </div>
    );
};

export default RequestedDays;
